import type { MessageBlock, ThreadMessage } from "@rakazo/contracts";
import { isPeerBlock, isToolActivityBlock, messageReaction } from "@rakazo/core";

export type BotMessageTarget = {
  peerBotId?: string;
  peerBotName?: string;
  messageId?: string;
};

export type BotMessageEntry = {
  key: string;
  messageId: string;
  peerBotId: string;
  peerBotName: string;
  direction: "sent" | "received";
  text: string;
  createdAt: string;
};

/** Recognize the internal report format without interpreting arbitrary JSON replies. */
export function botMessageReport(
  text: string,
): { summary: string; actions: string[]; evidence: string[] } | undefined {
  try {
    const raw = text.trim().replace(/^```json\s*\n([\s\S]*?)\n```$/, "$1");
    const report = JSON.parse(raw);
    if (
      report?.version !== 1 ||
      !["filed", "coordinator", "owner", "blocked"].includes(report.disposition) ||
      typeof report.summary !== "string" ||
      typeof report.proofPath !== "string" ||
      !report.proofPath.startsWith("notes/runner/") ||
      !Array.isArray(report.actions) ||
      !report.actions.every((item: unknown) => typeof item === "string") ||
      !Array.isArray(report.evidence) ||
      !report.evidence.every((item: unknown) => typeof item === "string")
    )
      return undefined;
    return { summary: report.summary, actions: report.actions, evidence: report.evidence };
  } catch {
    return undefined;
  }
}

/** Keep peer traffic inspectable without turning it into a second conversation in the chat. */
export function botMessageActivity(messages: readonly ThreadMessage[]) {
  const humanMessageIds = new Set(
    messages
      .filter((message) => message.role === "user" && !message.blocks.some(isPeerBlock))
      .map((message) => message.id),
  );
  const peerByRun = new Map<string, { id: string; name: string }>();
  for (const message of messages) {
    if (!message.runId) continue;
    const receipt = message.blocks.find((block) => block.kind === "bot_message_received");
    if (receipt?.kind === "bot_message_received") {
      peerByRun.set(message.runId, { id: receipt.fromBotId, name: receipt.fromBotName });
    }
  }
  const entries: BotMessageEntry[] = [];
  const inlineBlocks = new Map<string, MessageBlock[]>();
  for (const message of messages) {
    let hasReceipt = false;
    message.blocks.forEach((block, index) => {
      if (!isPeerBlock(block)) return;
      hasReceipt = true;
      const sent = block.kind === "bot_message_sent";
      entries.push({
        key: `${message.id}:${index}`,
        messageId: message.id,
        peerBotId: sent ? block.toBotId : block.fromBotId,
        peerBotName: sent ? block.toBotName : block.fromBotName,
        direction: sent ? "sent" : "received",
        text: block.text,
        createdAt: message.createdAt,
      });
    });
    const directedToHuman =
      message.replyToMessageId && humanMessageIds.has(message.replyToMessageId);
    const peer =
      message.runId && message.role === "bot" && !directedToHuman
        ? peerByRun.get(message.runId)
        : undefined;
    if (peer && !hasReceipt) {
      const text = message.blocks
        .filter((block) => block.kind === "text")
        .map((block) => block.text)
        .join("\n\n");
      if (text.trim())
        entries.push({
          key: `${message.id}:response`,
          messageId: message.id,
          peerBotId: peer.id,
          peerBotName: peer.name,
          direction: "sent",
          text,
          createdAt: message.createdAt,
        });
    }
    if (hasReceipt || peer) {
      inlineBlocks.set(
        message.id,
        message.blocks.filter(
          (block) =>
            !isPeerBlock(block) &&
            (!peer ||
              (block.kind !== "text" &&
                block.kind !== "progress" &&
                block.kind !== "steps" &&
                !isToolActivityBlock(block))),
        ),
      );
    }
  }
  for (const message of messages) {
    if (
      message.replyToMessageId &&
      inlineBlocks.get(message.replyToMessageId)?.length === 0 &&
      messageReaction(message)
    ) {
      inlineBlocks.set(message.id, []);
    }
  }
  return { entries: entries.sort((a, b) => b.createdAt.localeCompare(a.createdAt)), inlineBlocks };
}
